/**
 * V2 API Client Service
 *
 * Type-safe access to V2 API endpoints through API Gateway.
 * Configuration is automatically loaded from amplify_outputs.json.
 *
 * V2 API provides:
 * - Standardized response format with success, data, error, timestamp
 * - Server-side authorization checks (requires Cognito JWT token)
 * - Better error handling with specific error codes
 * - Version metadata in responses
 */

import { resolveOutputsFile } from '@/app/lib/outputs';
import { getIdToken } from '@/app/lib/auth';

/**
 * V2 API Response wrapper
 */
export interface ApiV2Response<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  timestamp: string;
  version: string;
}

/**
 * Account Key structure from V2 API
 */
export interface AccountKey {
  locationId: string;
  houseNumber: string;
  occupantCode: string;
}

/**
 * Physical Address structure
 */
export type PhysicalAddress = {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  apartmentNumber?: string;
}

/**
 * Money structure from V2 API
 */
export interface Money {
  type: string;
  amount: number;
}

/**
 * Customer from V2 API
 */
export interface CustomerV2 {
  accountKey?: AccountKey;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: PhysicalAddress;
  type?: string;
}

/**
 * Service Detail from V2 API
 */
export interface ServiceDetailV2 {
  serviceCode: string;
  billingDescription: string;
  serviceStatus: number;
  rate?: Money;
  alaCarteRate?: Money;
  quantity?: number;
  installDate?: string;
  disconnectDate?: string;
  startBillingDate?: string;
  billThroughDate?: string;
  billingFrequency?: string;
}

/**
 * Service Occurrence from V2 API
 */
export interface ServiceOccurrenceV2 {
  type: string;
  occurrenceNumber: number;
  occurrenceType: string;
  status: number;
  location?: string;
  installDate?: string;
  serviceDetails?: ServiceDetailV2[];
}

/**
 * Account from V2 API (GET /v2/accounts/:accountId)
 */
export interface AccountV2 {
  key: AccountKey;
  accountId: string;
  customer?: CustomerV2;
  balance: Money | null;
  balanceInDollars: number | null;
  status: 'A' | 'I' | 'S' | 'P' | null;
  services?: ServiceOccurrenceV2[];
}

/**
 * Account Balance Response (GET /v2/accounts/:accountId/balance)
 */
export interface AccountBalanceV2 {
  accountId: string;
  balance: Money | null;
  balanceInDollars: number | null;
}

/**
 * Account Services Response (GET /v2/accounts/:accountId/services)
 */
export interface AccountServicesV2 {
  accountId: string;
  services: ServiceOccurrenceV2[];
}

export class ApiV2Error extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiV2Error';
  }
}

let cachedConfig: { endpoint: string; apiKey: string } | null = null;

async function getApiV2Config(): Promise<{ endpoint: string; apiKey: string }> {
  if (cachedConfig) {
    return cachedConfig;
  }

  const outputs = await resolveOutputsFile();

  // Read API Gateway URL from outputs
  const endpoint = outputs.default?.custom?.apiGatewayUrl;

  // Read API key from outputs first, fall back to env var
  const apiKey =
    outputs.default?.custom?.portalFrontendApiKey ||
    process.env.NEXT_PUBLIC_ICAPI_API_KEY ||
    '';

  if (!endpoint) {
    throw new ApiV2Error(
      'API endpoint not found in amplify_outputs.json',
      'CONFIG_ERROR'
    );
  }

  if (!apiKey) {
    throw new ApiV2Error(
      'API key not found in amplify_outputs.json',
      'CONFIG_ERROR'
    );
  }

  cachedConfig = { endpoint, apiKey };
  return cachedConfig;
}

/**
 * Generic V2 API fetch wrapper
 *
 * Automatically includes:
 * - x-api-key header for API Gateway authentication
 * - Authorization header with Cognito JWT token for user authentication
 */
async function fetchV2<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const config = await getApiV2Config();
  const url = `${config.endpoint}${path}`;

  // Get Cognito ID token for authenticated requests
  const idToken = await getIdToken();

  // Build headers with optional Authorization
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': config.apiKey,
  };

  // Add Authorization header if user is authenticated
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  const data = await response.json() as ApiV2Response<T>;

  if (!response.ok || !data.success) {
    const error = data.error || { code: 'UNKNOWN_ERROR', message: 'Request failed' };
    throw new ApiV2Error(
      error.message,
      error.code,
      response.status,
      error.details
    );
  }

  return data.data as T;
}

// ============================================================================
// Account Endpoints
// ============================================================================

/**
 * Get account by account ID (V2 API)
 *
 * Retrieves complete account information including customer details,
 * balance, status, and services in a single call.
 *
 * @param accountId - Account ID (e.g., '7777777')
 * @returns Promise resolving to full account data
 * @throws ApiV2Error if the request fails
 *
 * @example
 * const account = await getAccountV2('7777777');
 * console.log(account.customer?.name); // "John Doe"
 * console.log(account.balanceInDollars); // 125.50
 * console.log(account.services?.length); // 3
 */
export async function getAccountV2(accountId: string): Promise<AccountV2> {
  return fetchV2<AccountV2>(`v2/accounts/${accountId}`);
}

/**
 * Get account balance only (V2 API)
 *
 * Lightweight endpoint to get just the account balance.
 *
 * @param accountId - Account ID
 * @returns Promise resolving to balance data
 * @throws ApiV2Error if the request fails
 */
export async function getAccountBalanceV2(accountId: string): Promise<AccountBalanceV2> {
  return fetchV2<AccountBalanceV2>(`v2/accounts/${accountId}/balance`);
}

/**
 * Get account services (V2 API)
 *
 * Retrieves list of services for an account.
 *
 * @param accountId - Account ID
 * @returns Promise resolving to services data
 * @throws ApiV2Error if the request fails
 */
export async function getAccountServicesV2(accountId: string): Promise<AccountServicesV2> {
  return fetchV2<AccountServicesV2>(`v2/accounts/${accountId}/services`);
}

/**
 * Nova Eligibility Response
 */
export interface NovaEligibilityV2 {
  accountId: string;
  eligible: boolean;
  marketingId: string | null;
}

/**
 * Check Nova fiber upgrade eligibility (V2 API)
 *
 * Checks ICOMS marketing ID records for NOVA_PHASE1.
 *
 * @param accountId - Account ID
 * @returns Promise resolving to eligibility status
 * @throws ApiV2Error if the request fails
 */
export async function checkNovaEligibilityV2(accountId: string): Promise<NovaEligibilityV2> {
  return fetchV2<NovaEligibilityV2>(`v2/accounts/${accountId}/nova-eligibility`);
}

/**
 * Autopay Policy Response
 */
export interface AutopayPolicyV2 {
  accountId: string;
  autopayRequired: boolean;
}

/**
 * Check whether Bluepeak's MDU policy requires this account to be on
 * autopay (V2 API, backed by the Tortuga ICOMS autopay-required endpoint).
 *
 * @param accountId - Account ID
 * @returns Promise resolving to the policy flag
 * @throws ApiV2Error if the request fails
 */
export async function checkAutopayPolicyV2(accountId: string): Promise<AutopayPolicyV2> {
  return fetchV2<AutopayPolicyV2>(`v2/accounts/${accountId}/autopay-policy`);
}

/**
 * Fiber install schedule slot — one 2-hour install window returned by
 * `GET /v2/fiber/{accountId}/schedule`. `startTime` / `endTime` are ISO
 * strings with the ICOMS-local offset (e.g. `2026-06-22T10:00:00.000-05:00`).
 */
export type FiberInstallScheduleSlotV2 = {
  id: string;
  startTime: string;
  endTime: string | null;
  description?: string;
};

export type FiberInstallScheduleV2 = {
  accountId: string;
  orderNumber: string;
  orderPoints: number;
  slots: FiberInstallScheduleSlotV2[];
};

/** ISO 8601 search window forwarded to the schedule endpoints. */
export type FiberScheduleWindow = { start: string; end: string };

/**
 * Get available fiber install windows for an account that has already
 * acknowledged the Nova upgrade.
 *
 * @param accountId - Account ID
 * @param window - Optional ISO date range (the calendar month being viewed).
 *   Omitted → ICOMS's 7-day default.
 * @returns Promise resolving to the slot list
 * @throws ApiV2Error 409 NOT_READY if the account has no acknowledged upgrade
 */
export async function getFiberInstallScheduleV2(
  accountId: string,
  window?: FiberScheduleWindow,
): Promise<FiberInstallScheduleV2> {
  const qs = window
    ? `?start=${encodeURIComponent(window.start)}&end=${encodeURIComponent(window.end)}`
    : '';
  return fetchV2<FiberInstallScheduleV2>(`v2/fiber/${accountId}/schedule${qs}`);
}

export type NovaUpgradeStatus =
  | 'ACKNOWLEDGED'
  | 'SCHEDULED'
  | 'INSTALL_SRO_CREATED'
  | 'COMPLETED'
  | 'CANCELLED';

/**
 * The canonical fiber-upgrade record. Derived live from ICAPI SROs by the
 * backend (no DynamoDB): drop-bury (call code 77) ⇒ ACKNOWLEDGED; install
 * placeholder (call code 76) ⇒ INSTALL_SRO_CREATED, or SCHEDULED with an
 * appointment. Only `status`, `orderNumber`, `scheduledAt`, `acknowledgedAt`,
 * and `accountId` are read by the UI; the rest are for shape compatibility.
 */
export type NovaUpgradeRecord = {
  id: string;
  accountId: string;
  userId: string;
  status: NovaUpgradeStatus;
  orderNumber?: string | null;
  upgradeSroOrderNumber?: string | null;
  acknowledgedAt: string;
  scheduledAt?: string | null;
  sroCreatedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Alias kept for the acknowledge/book mutation return sites. */
export type NovaUpgradeAcknowledgeV2 = NovaUpgradeRecord;

/**
 * Acknowledge Nova fiber upgrade (V2 API)
 *
 * Triggers the full pipeline: eligibility re-check, dedup, SRO creation in ICOMS.
 *
 * @param accountId - Account ID
 * @returns Promise resolving to upgrade acknowledgement with order number
 * @throws ApiV2Error if eligibility check fails, duplicate exists, or ICAPI errors
 */
export async function acknowledgeNovaUpgradeV2(accountId: string): Promise<NovaUpgradeAcknowledgeV2> {
  return fetchV2<NovaUpgradeAcknowledgeV2>(`v2/accounts/${accountId}/nova-upgrade/acknowledge`, {
    method: 'POST',
  });
}

/**
 * Book a fiber install window (V2 API)
 *
 * Locks the chosen `timeSlotCode` onto the customer's Nova SRO and transitions
 * the upgrade record to `SCHEDULED`. Returns the updated record.
 *
 * @param accountId - Account ID
 * @param timeSlotCode - The slot `id` from `getFiberInstallScheduleV2`
 * @throws ApiV2Error 409 NOT_READY (no acknowledged upgrade) or SLOT_UNAVAILABLE
 *   (the window was taken between search and confirm)
 */
export async function bookFiberInstallV2(
  accountId: string,
  timeSlotCode: string,
  window?: FiberScheduleWindow,
): Promise<NovaUpgradeAcknowledgeV2> {
  return fetchV2<NovaUpgradeAcknowledgeV2>(`v2/fiber/${accountId}/schedule`, {
    method: 'POST',
    body: JSON.stringify({
      timeSlotCode,
      ...(window ? { start: window.start, end: window.end } : {}),
    }),
  });
}

/**
 * Get the account's fiber-upgrade state, derived live from ICAPI SROs. This is
 * the single source of truth for the upgrade stage (no DynamoDB). Returns the
 * derived record, or `null` when the account has no active Nova SRO (pre-ack).
 *
 * @param accountId - Account ID
 */
export async function getUpgradeStateV2(
  accountId: string,
): Promise<NovaUpgradeRecord | null> {
  return fetchV2<NovaUpgradeRecord | null>(`v2/fiber/${accountId}/upgrade-state`);
}

/** Response shape of `POST /v2/fiber/{accountId}/schedule/prepare`. */
export type PrepareUpgradeSchedulingV2 = {
  orderNumber: string;
  status: NovaUpgradeStatus;
};

/**
 * Ensure the install placeholder SRO (call code 76) exists for the account —
 * the order the calendar and booking operate on. Called when the scheduler
 * first renders; idempotent, so it's safe to call on every mount and never
 * creates a duplicate placeholder.
 */
export async function prepareUpgradeSchedulingV2(
  accountId: string,
): Promise<PrepareUpgradeSchedulingV2> {
  return fetchV2<PrepareUpgradeSchedulingV2>(`v2/fiber/${accountId}/schedule/prepare`, {
    method: 'POST',
  });
}

// ============================================================================
// Billing Endpoints
// ============================================================================

/**
 * Pending Balance data from V2 API
 */
export interface PendingBalanceData {
  accountId: string;
  amount: Money;
  amountInDollars: number;
}

/**
 * Pending Balance response wrapper from V2 API
 * The API returns pendingBalance nested inside data
 */
export interface PendingBalanceV2 {
  pendingBalance: PendingBalanceData;
}

/**
 * Get pending payment balance (V2 API)
 *
 * Retrieves the total pending payment amount for an account.
 * This includes payments that have been submitted but not yet posted.
 *
 * @param accountId - Account ID in composite format (e.g., 'CLRTEBPP_001-36705-06')
 * @returns Promise resolving to pending balance data
 * @throws ApiV2Error if the request fails
 *
 * @example
 * const pending = await getPendingBalanceV2('CLRTEBPP_001-36705-06');
 * console.log(`Pending: $${pending.pendingBalance.amountInDollars}`); // "Pending: $55.00"
 */
export async function getPendingBalanceV2(accountId: string): Promise<PendingBalanceV2> {
  return fetchV2<PendingBalanceV2>(`v2/billing/${accountId}/pending-balance`);
}

// ============================================================================
// Billing Domain Types (V2)
// ============================================================================

/**
 * Bill Item - Individual line items on a bill or statement
 */
export interface BillItemV2 {
  description: string;
  amount: Money;
  quantity?: number;
  unitPrice?: Money;
  serviceCode?: string;
  category?: string;
}

/**
 * Bill - Billing statement summary
 */
export interface BillV2 {
  billId: string;
  accountId: string;
  billDate: string; // ISO 8601 date
  dueDate: string; // ISO 8601 date
  totalAmount: Money;
  balanceDue: Money;
  previousBalance?: Money;
  payments?: Money;
  adjustments?: Money;
  newCharges?: Money;
  items?: BillItemV2[];
  statementNumber?: string;
}

/**
 * Statement - Detailed billing statement with line items
 */
export interface StatementV2 {
  statementId: string;
  accountId: string;
  statementDate: string | null; // ISO 8601 date, null if not yet generated
  billingPeriodStart: string | null; // ISO 8601 date, null if not yet generated
  billingPeriodEnd: string | null; // ISO 8601 date, null if not yet generated
  dueDate: string | null; // ISO 8601 date, null if not yet generated
  /** Due date type: 1=Standard (show date), 2=On Receipt (show "Due Now"), null if unknown */
  dueDateType?: number | null;
  totalAmount: Money;
  balanceDue: Money;
  previousBalance: Money;
  paymentsReceived: Money;
  adjustments: Money;
  currentCharges: Money;
  items: BillItemV2[];
  statementNumber?: string;
  billCycle?: string;
  /** Delivery preference code: PAPER, ELECTRONIC, etc. */
  deliveryCode?: 'ELECTRONIC' | 'PAPER' | string;
  /** Whether paperless billing is enabled */
  paperlessEnabled?: boolean;
}

/**
 * AR Aging Buckets - Breakdown of accounts receivable by age
 */
export interface ARAgingBuckets {
  ar1To30: Money; // AR 1-30 days (current bill, due on due date)
  ar31To60: Money; // AR 31-60 days (past due 1 bill)
  ar61To90: Money; // AR 61-90 days (past due 2 bills)
  ar91To120: Money; // AR 91-120 days (past due 3 bills)
  ar121To150: Money; // AR 121-150 days (past due 4 bills)
  arOver150Days: Money; // AR over 150 days (past due 5+ bills)
}

/**
 * Balance - Current account balance information
 *
 * IMPORTANT: balanceDue is the correct amount to display to customers.
 * It equals Total AR Balance minus Unbilled Charges.
 */
export interface BalanceV2 {
  accountId: string;
  currentBalance: Money; // Total AR Balance (includes unbilled charges) - for reference
  balanceDue: Money; // Balance Due = Total AR - Unbilled (what to display to customer)
  unbilledCharges?: Money; // Charges not yet appearing on a bill
  pastDueBalance?: Money; // Amount past due (31+ days) - for soft disconnect status
  pendingPayments?: Money;
  lastPaymentDate?: string; // ISO 8601 date
  lastPaymentAmount?: Money;
  nextDueDate?: string; // ISO 8601 date
  lastStatementBalance?: Money; // Last printed statement balance (frozen, doesn't change with payments)
  lastStatementDate?: string; // ISO 8601 date — when last statement was printed
  arAging?: ARAgingBuckets; // Detailed AR aging breakdown
}

/**
 * Paperless Billing Preference
 */
export interface PaperlessBillingPreferenceV2 {
  accountId: string;
  paperlessEnabled: boolean;
  email?: string;
  lastUpdated?: string; // ISO 8601 date
}

// ============================================================================
// Billing Response Types (V2)
// ============================================================================

/**
 * Response from GET /v2/billing/{accountId}/balance
 */
export interface BillingBalanceResponseV2 {
  balance: BalanceV2;
}

/**
 * Response from GET /v2/billing/{accountId}/statements
 */
export interface BillingStatementsResponseV2 {
  statements: StatementV2[];
  requestedPeriod?: {
    year?: number;
    month?: number;
  };
}

/**
 * Response from GET /v2/billing/{accountId}/bills (search)
 */
export interface BillsSearchResponseV2 {
  bills: BillV2[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
  count: number;
}

/**
 * Response from GET /v2/billing/{accountId}/bills/{billId}
 */
export interface BillDetailsResponseV2 {
  bill: BillV2;
}

/**
 * Response from PUT /v2/billing/{accountId}/preferences/paperless
 */
export interface PaperlessPreferenceResponseV2 {
  preference: PaperlessBillingPreferenceV2;
}

// ============================================================================
// Billing Service Functions (V2)
// ============================================================================

/**
 * Get account balance from billing domain (V2 API)
 *
 * Returns comprehensive balance information including:
 * - balanceDue: What to display to customers
 * - currentBalance: Total AR including unbilled charges
 * - pastDueBalance: For soft disconnect checks
 * - arAging: Breakdown by age buckets
 *
 * @param accountId - Account ID (e.g., '7777777' or 'CLRTEBPP_001-81310-01')
 * @returns Promise resolving to balance data
 * @throws ApiV2Error if the request fails
 *
 * @example
 * const { balance } = await getBillingBalanceV2('7777777');
 * console.log(`Balance Due: $${balance.balanceDue.amount / 100}`);
 */
export async function getBillingBalanceV2(accountId: string): Promise<BillingBalanceResponseV2> {
  return fetchV2<BillingBalanceResponseV2>(`v2/billing/${accountId}/balance`);
}

/**
 * Get billing statements (V2 API)
 *
 * Retrieves detailed billing statements with line items.
 * Optionally filter by year and/or month.
 *
 * @param accountId - Account ID
 * @param year - Optional year filter (2000-2100)
 * @param month - Optional month filter (1-12)
 * @returns Promise resolving to statements data
 * @throws ApiV2Error if the request fails
 *
 * @example
 * // Get all statements
 * const { statements } = await getBillingStatementsV2('7777777');
 *
 * // Get statements for specific month
 * const { statements } = await getBillingStatementsV2('7777777', 2025, 1);
 */
export async function getBillingStatementsV2(
  accountId: string,
  year?: number,
  month?: number
): Promise<BillingStatementsResponseV2> {
  const params = new URLSearchParams();
  if (year !== undefined) params.append('year', year.toString());
  if (month !== undefined) params.append('month', month.toString());

  const queryString = params.toString();
  const path = `v2/billing/${accountId}/statements${queryString ? `?${queryString}` : ''}`;

  return fetchV2<BillingStatementsResponseV2>(path);
}

/**
 * Search bills (V2 API)
 *
 * Search for bills within an optional date range.
 * Defaults to last 12 months if no dates provided.
 *
 * @param accountId - Account ID
 * @param startDate - Optional start date (YYYY-MM-DD format)
 * @param endDate - Optional end date (YYYY-MM-DD format)
 * @returns Promise resolving to bills search results
 * @throws ApiV2Error if the request fails
 *
 * @example
 * const { bills, count } = await searchBillsV2('7777777', '2024-01-01', '2025-01-01');
 */
export async function searchBillsV2(
  accountId: string,
  startDate?: string,
  endDate?: string
): Promise<BillsSearchResponseV2> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const queryString = params.toString();
  const path = `v2/billing/${accountId}/bills${queryString ? `?${queryString}` : ''}`;

  return fetchV2<BillsSearchResponseV2>(path);
}

/**
 * Get bill details (V2 API)
 *
 * Retrieves detailed information for a specific bill.
 *
 * @param accountId - Account ID
 * @param billId - Bill ID
 * @returns Promise resolving to bill details
 * @throws ApiV2Error if the request fails
 *
 * @example
 * const { bill } = await getBillDetailsV2('7777777', 'BILL-123');
 */
export async function getBillDetailsV2(
  accountId: string,
  billId: string
): Promise<BillDetailsResponseV2> {
  return fetchV2<BillDetailsResponseV2>(`v2/billing/${accountId}/bills/${billId}`);
}

/**
 * Update paperless billing preference (V2 API)
 *
 * Enable or disable paperless billing for an account.
 *
 * @param accountId - Account ID
 * @param enabled - Whether paperless billing should be enabled
 * @param email - Optional email address for paperless notifications
 * @returns Promise resolving to updated preference
 * @throws ApiV2Error if the request fails
 *
 * @example
 * // Enable paperless billing
 * const { preference } = await updatePaperlessBillingV2('7777777', true, 'user@example.com');
 *
 * // Disable paperless billing
 * const { preference } = await updatePaperlessBillingV2('7777777', false);
 */
export async function updatePaperlessBillingV2(
  accountId: string,
  enabled: boolean,
  email?: string
): Promise<PaperlessPreferenceResponseV2> {
  const body: { enabled: boolean; email?: string } = { enabled };
  if (email) body.email = email;

  return fetchV2<PaperlessPreferenceResponseV2>(
    `v2/billing/${accountId}/preferences/paperless`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    }
  );
}

// ============================================================================
// Payment Types
// ============================================================================

/**
 * Payment Method Key
 */
export interface PaymentMethodKey {
  accountKey: {
    locationId: string;
    houseNumber: string;
    occupantCode: string;
  };
  methodOfPaymentCode: string;
  accountMethodOfPaymentSequence: number;
}

/**
 * Payment Method (credit card or bank account)
 */
export interface PaymentMethodV2 {
  key: PaymentMethodKey;
  description: string;
  status: string;
  paymentType?: 'credit_card' | 'bank_account';
  isAutoPay?: boolean;
  isDefault?: boolean;
  // Cardholder / account holder name
  firstName?: string;
  lastName?: string;
  // Credit card fields
  cardType?: string;
  cardNumber?: string;
  expirationMonth?: string;
  expirationYear?: string;
  // Bank account fields
  accountType?: string;
  routingNumber?: string;
  accountNumber?: string;
}

/**
 * AutoPay Settings
 */
export interface AutoPaySettingsV2 {
  enabled: boolean;
  paymentMethodKey?: PaymentMethodKey;
  statementCode?: string;
}

/**
 * Query Payment Methods Response
 */
export interface QueryPaymentMethodsResponseV2 {
  accountId: string;
  paymentMethods: PaymentMethodV2[];
}

/**
 * Query AutoPay Response
 */
export interface QueryAutoPayResponseV2 {
  accountId: string;
  autoPayEnabled: boolean;
  autoPaySettings?: AutoPaySettingsV2;
}

/**
 * Enable/Disable AutoPay Request
 */
export interface AutoPayRequestV2 {
  methodOfPaymentCode: string;
  accountMethodOfPaymentSequence: number;
  statementCode?: string;
}

/**
 * Enable/Disable AutoPay Response
 */
export interface AutoPayResponseV2 {
  success: boolean;
  message: string;
  autoPaySettings: AutoPaySettingsV2;
}

/**
 * Create Credit Card Request
 */
export interface CreateCreditCardRequestV2 {
  cardNumber: string;
  expirationDate: string;
  firstName: string;
  lastName: string;
  description?: string;
  methodOfPaymentCode?: string;
}

/**
 * Create Bank Account Request
 */
export interface CreateBankAccountRequestV2 {
  accountNumber: string;
  routingNumber: string;
  accountType: 'C' | 'S';
  firstName: string;
  lastName: string;
  description?: string;
  methodOfPaymentCode?: string;
}

/**
 * Create Payment Method Response
 */
export interface CreatePaymentMethodResponseV2 {
  success: boolean;
  message: string;
  paymentMethodKey: {
    locationId: string;
    houseNumber: string;
    occupantCode: string;
    methodOfPaymentCode: string;
    accountMethodOfPaymentSequence: number;
  };
}

/**
 * Delete Payment Method Response
 */
export interface DeletePaymentMethodResponseV2 {
  success: boolean;
  message: string;
}

/**
 * Update Payment Method Request
 *
 * At least one field must be provided.
 * Used to update credit card expiration date and cardholder name
 * without needing to delete and re-create the payment method.
 */
export interface UpdatePaymentMethodRequestV2 {
  expirationDate?: string;
  firstName?: string;
  lastName?: string;
  description?: string;
}

/**
 * Update Payment Method Response
 */
export interface UpdatePaymentMethodResponseV2 {
  success: boolean;
  message: string;
  paymentMethod: PaymentMethodV2;
}

/**
 * Make Payment Request
 */
export interface MakePaymentRequestV2 {
  amount: number;
  methodOfPaymentCode: string;
  accountMethodOfPaymentSequence: number;
  statementCode?: string;
  receiptType?: string;
}

/**
 * Make Payment Response
 */
export interface MakePaymentResponseV2 {
  success: boolean;
  message: string;
  paymentKey?: {
    locationId?: string;
    houseNumber?: string;
    occupantCode?: string;
    paymentNumber?: number;
  };
  amountTendered: {
    type: string;
    amount: number;
  };
  amountApplied: {
    type: string;
    amount: number;
  };
  status?: string;
}

/**
 * Query Last Payment Response
 */
export interface QueryLastPaymentResponseV2 {
  accountId: string;
  lastPayment: {
    amount: number;
    date: string;
  } | null;
}

/**
 * Query Recurring Payment Method Response
 */
export interface QueryRecurringPaymentMethodResponseV2 {
  accountId: string;
  recurringPaymentMethod: PaymentMethodV2 | null;
}

/**
 * Last Payment data from V2 API (legacy alias)
 */
export interface LastPaymentV2 {
  accountId: string;
  lastPayment: {
    amount: number; // Amount in cents (positive value)
    date: string; // ISO 8601 date string (e.g., "2024-09-15")
  } | null;
}

// ============================================================================
// Payment Endpoints
// ============================================================================

/**
 * Query payment methods for an account (V2 API)
 *
 * Returns all payment methods including credit cards and bank accounts.
 * Replaces separate getCreditCards and getBankAccounts calls.
 *
 * @param accountId - Account ID
 * @returns Promise resolving to payment methods
 * @throws ApiV2Error if the request fails
 */
export async function queryPaymentMethodsV2(accountId: string): Promise<QueryPaymentMethodsResponseV2> {
  return fetchV2<QueryPaymentMethodsResponseV2>(`v2/payments/${accountId}/methods`);
}

/**
 * Query autopay status for an account (V2 API)
 *
 * @param accountId - Account ID
 * @returns Promise resolving to autopay settings
 * @throws ApiV2Error if the request fails
 */
export async function queryAutoPayV2(accountId: string): Promise<QueryAutoPayResponseV2> {
  return fetchV2<QueryAutoPayResponseV2>(`v2/payments/${accountId}/autopay`);
}

/**
 * Enable autopay for a payment method (V2 API)
 *
 * @param accountId - Account ID
 * @param request - Enable autopay request
 * @returns Promise resolving to autopay response
 * @throws ApiV2Error if the request fails
 */
export async function enableAutoPayV2(
  accountId: string,
  request: AutoPayRequestV2
): Promise<AutoPayResponseV2> {
  return fetchV2<AutoPayResponseV2>(`v2/payments/${accountId}/autopay/enable`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Disable autopay for a payment method (V2 API)
 *
 * @param accountId - Account ID
 * @param request - Disable autopay request
 * @returns Promise resolving to autopay response
 * @throws ApiV2Error if the request fails
 */
export async function disableAutoPayV2(
  accountId: string,
  request: AutoPayRequestV2
): Promise<AutoPayResponseV2> {
  return fetchV2<AutoPayResponseV2>(`v2/payments/${accountId}/autopay/disable`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Create a credit card payment method (V2 API)
 *
 * @param accountId - Account ID
 * @param request - Credit card details
 * @returns Promise resolving to create response
 * @throws ApiV2Error if the request fails
 */
export async function createCreditCardV2(
  accountId: string,
  request: CreateCreditCardRequestV2
): Promise<CreatePaymentMethodResponseV2> {
  return fetchV2<CreatePaymentMethodResponseV2>(`v2/payments/${accountId}/methods/credit-card`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Create a bank account payment method (V2 API)
 *
 * @param accountId - Account ID
 * @param request - Bank account details
 * @returns Promise resolving to create response
 * @throws ApiV2Error if the request fails
 */
export async function createBankAccountV2(
  accountId: string,
  request: CreateBankAccountRequestV2
): Promise<CreatePaymentMethodResponseV2> {
  return fetchV2<CreatePaymentMethodResponseV2>(`v2/payments/${accountId}/methods/bank-account`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a payment method (V2 API)
 *
 * @param accountId - Account ID
 * @param methodOfPaymentCode - Payment method code (e.g., "510" for Visa)
 * @param sequence - Payment method sequence number
 * @returns Promise resolving to delete response
 * @throws ApiV2Error if the request fails
 */
export async function deletePaymentMethodV2(
  accountId: string,
  methodOfPaymentCode: string,
  sequence: number
): Promise<DeletePaymentMethodResponseV2> {
  const methodId = `${methodOfPaymentCode}-${sequence}`;
  return fetchV2<DeletePaymentMethodResponseV2>(`v2/payments/${accountId}/methods/${methodId}`, {
    method: 'DELETE',
  });
}

/**
 * Update an existing payment method (V2 API)
 *
 * Updates credit card expiration date and/or cardholder name without
 * needing to delete and re-create the payment method. Works on autopay
 * methods without needing to disable autopay first.
 *
 * @param accountId - Account ID
 * @param methodOfPaymentCode - Payment method code (e.g., "510" for Visa)
 * @param sequence - Payment method sequence number
 * @param request - Fields to update (at least one required)
 * @returns Promise resolving to updated payment method
 * @throws ApiV2Error if the request fails
 */
export async function updatePaymentMethodV2(
  accountId: string,
  methodOfPaymentCode: string,
  sequence: number,
  request: UpdatePaymentMethodRequestV2
): Promise<UpdatePaymentMethodResponseV2> {
  const methodId = `${methodOfPaymentCode}-${sequence}`;
  return fetchV2<UpdatePaymentMethodResponseV2>(`v2/payments/${accountId}/methods/${methodId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Make a one-time payment (V2 API)
 *
 * @param accountId - Account ID
 * @param request - Payment details
 * @returns Promise resolving to payment response
 * @throws ApiV2Error if the request fails
 */
export async function makePaymentV2(
  accountId: string,
  request: MakePaymentRequestV2
): Promise<MakePaymentResponseV2> {
  return fetchV2<MakePaymentResponseV2>(`v2/payments/${accountId}/make-payment`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Query last payment for an account (V2 API)
 *
 * Retrieves the most recent payment received on an account.
 * Uses MAC00029 with INL00165/INL00166 inlines.
 *
 * Key differences from V1:
 * - Amount is returned as a positive number (V1 returns negative)
 * - Date is returned as ISO 8601 string (V1 returns epoch instant)
 *
 * @param accountId - Account ID (e.g., '7777777' or 'CLRTEBPP_001-81310-01')
 * @returns Promise resolving to last payment data
 * @throws ApiV2Error if the request fails
 *
 * @example
 * const { lastPayment } = await getLastPaymentV2('7777777');
 * if (lastPayment) {
 *   console.log(`Last payment: $${(lastPayment.amount / 100).toFixed(2)} on ${lastPayment.date}`);
 * }
 */
export async function getLastPaymentV2(accountId: string): Promise<LastPaymentV2> {
  return fetchV2<LastPaymentV2>(`v2/payments/${accountId}/last-payment`);
}

// Alias for backwards compatibility with use-payments-v2.ts
export const queryLastPaymentV2 = getLastPaymentV2;

/**
 * Query recurring payment method (autopay method) for an account (V2 API)
 *
 * @param accountId - Account ID
 * @returns Promise resolving to recurring payment method
 * @throws ApiV2Error if the request fails
 */
export async function queryRecurringPaymentMethodV2(accountId: string): Promise<QueryRecurringPaymentMethodResponseV2> {
  return fetchV2<QueryRecurringPaymentMethodResponseV2>(`v2/payments/${accountId}/recurring-payment-method`);
}

/**
 * Query one-time/ephemeral payment methods (V2 API)
 *
 * Returns only one-time payment methods (MOP codes ending in 1).
 *
 * @param accountId - Account ID
 * @returns Promise resolving to one-time payment methods
 * @throws ApiV2Error if the request fails
 */
export async function queryOneTimeMethodsV2(accountId: string): Promise<QueryPaymentMethodsResponseV2> {
  return fetchV2<QueryPaymentMethodsResponseV2>(`v2/payments/${accountId}/one-time-methods`);
}

/**
 * Search Duplicate Method Request
 */
export interface SearchDuplicateMethodRequestV2 {
  last4: string;
  cardType: string;
}

/**
 * Search Duplicate Method Response
 */
export interface SearchDuplicateMethodResponseV2 {
  accountId: string;
  match: {
    methodOfPaymentCode: string;
    accountMethodOfPaymentSequence: number;
    description: string;
    cardType?: string;
  } | null;
}

/**
 * Search for a duplicate recurring payment method (V2 API)
 *
 * Searches saved/recurring methods for a card matching last 4 digits + card type.
 * Returns the matching method's key if found, allowing reuse instead of creating
 * a new one-time method.
 *
 * @param accountId - Account ID
 * @param request - Card details for matching
 * @returns Promise resolving to match result
 * @throws ApiV2Error if the request fails
 */
export async function searchDuplicateMethodV2(
  accountId: string,
  request: SearchDuplicateMethodRequestV2
): Promise<SearchDuplicateMethodResponseV2> {
  return fetchV2<SearchDuplicateMethodResponseV2>(
    `v2/payments/${accountId}/methods/search-duplicates`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

// ============================================================================
// Account Update Types
// ============================================================================

/**
 * Update Customer Contact Request (V2 API)
 *
 * At least one field must be provided.
 * Backend maps phone to homePhone in ICAPI.
 */
export interface UpdateCustomerContactV2Input {
  accountId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

/**
 * Update Customer Contact Response (V2 API)
 */
export interface UpdateCustomerContactV2Response {
  success: boolean;
  message: string;
  customer: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

// ============================================================================
// Account Update Endpoints
// ============================================================================

/**
 * Update customer contact information (V2 API)
 *
 * Updates customer contact fields: firstName, lastName, email, phone.
 * At least one field must be provided.
 *
 * Backend uses MAC00054 ICOMS macro for reliable persistence.
 * Phone is mapped to homePhone in ICAPI.
 *
 * @param input - Account ID and contact fields to update
 * @returns Promise resolving to update result
 * @throws ApiV2Error if the request fails or validation fails
 *
 * @example
 * // Update email only
 * const result = await updateCustomerContactV2({
 *   accountId: '7777777',
 *   email: 'newemail@example.com'
 * });
 *
 * // Update multiple fields
 * const result = await updateCustomerContactV2({
 *   accountId: '7777777',
 *   firstName: 'Jane',
 *   lastName: 'Doe',
 *   phone: '555-1234'
 * });
 */
export async function updateCustomerContactV2(
  input: UpdateCustomerContactV2Input
): Promise<UpdateCustomerContactV2Response> {
  const { accountId, ...updateFields } = input;

  return fetchV2<UpdateCustomerContactV2Response>(
    `v2/accounts/${accountId}/customer`,
    {
      method: 'PUT',
      body: JSON.stringify(updateFields),
    }
  );
}

// ============================================================================
// Chat Endpoints
// ============================================================================

/**
 * Front Chat Identity Hash Response
 */
export interface FrontChatIdentityHashResponse {
  email: string;
  userHash: string;
}

/**
 * Get Front Chat identity verification hash (V2 API)
 *
 * Returns an HMAC-SHA256 hash of the authenticated user's email for
 * Front Chat identity verification. The email is extracted server-side
 * from the Cognito JWT — no email parameter is needed.
 *
 * @returns Promise resolving to email and userHash
 * @throws ApiV2Error if not authenticated or secret not configured
 */
export async function getFrontChatIdentityHash(): Promise<FrontChatIdentityHashResponse> {
  return fetchV2<FrontChatIdentityHashResponse>('v2/chat/identity-hash');
}

// ============================================================================
// Utility Re-exports
// ============================================================================
// Note: For formatting Money amounts, use formatAmountToDollars from '@/amplify/types/icapi'
// Example: formatAmountToDollars(money.amount) returns "$125.50"
